import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';
import './SupportConsole.css';

const STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];

/**
 * Platform-staff support console: searchable/filterable ticket list, and a detail view with the
 * full conversation (customer messages, support replies, internal notes, SYSTEM lifecycle events),
 * reply / internal-note composer, and status control. Backed by /api/admin/support/tickets.
 */
function SupportConsole() {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);   // full detail of the open ticket
  const [loading, setLoading] = useState(false);
  const [composer, setComposer] = useState('');
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (pageArg = 0) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/admin/support/tickets', {
        params: { q: q || undefined, status: statusFilter || undefined, page: pageArg, size: 20 },
      });
      setTickets(data.tickets || []);
      setTotalPages(data.totalPages || 0);
      setPage(data.page || 0);
    } catch {
      setError(t('SupportConsole.loadError', 'Could not load tickets.'));
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, t]);

  useEffect(() => { load(0); }, [statusFilter]);          // eslint-disable-line react-hooks/exhaustive-deps

  const openTicket = async (id) => {
    setError('');
    try {
      const { data } = await axiosInstance.get(`/admin/support/tickets/${id}`);
      setSelected(data);
      setComposer('');
      setInternal(false);
    } catch {
      setError(t('SupportConsole.loadError', 'Could not load tickets.'));
    }
  };

  const sendMessage = async () => {
    if (!composer.trim()) return;
    setSending(true);
    setError('');
    try {
      await axiosInstance.post(`/admin/support/tickets/${selected.id}/messages`, {
        body: composer.trim(),
        internal,
      });
      setComposer('');
      await openTicket(selected.id);
    } catch {
      setError(t('SupportConsole.sendError', 'Could not send the message.'));
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status) => {
    setError('');
    try {
      await axiosInstance.patch(`/admin/support/tickets/${selected.id}/status`, { status });
      await openTicket(selected.id);
      load(page);
    } catch {
      setError(t('SupportConsole.statusError', 'Could not change the status.'));
    }
  };

  const fmt = (s) => (s && s !== 'null' ? s.replace('T', ' ').slice(0, 16) : '—');

  if (selected) {
    return (
      <div className="spc-page">
        <button className="spc-back" onClick={() => { setSelected(null); load(page); }}>
          <i className="fas fa-arrow-left" /> {t('SupportConsole.back', 'All tickets')}
        </button>
        {error && <p className="spc-error">{error}</p>}

        <div className="spc-detail-head">
          <div>
            <h2>{selected.ticketNumber}</h2>
            <div className="spc-meta">
              <span><i className="fas fa-user" /> {selected.requesterName || '—'} &lt;{selected.requesterEmail}&gt;
                {!selected.hasAccount && <em className="spc-unverified"> ({t('SupportConsole.noAccount', 'no account — replies go by email')})</em>}
              </span>
              <span><i className="fas fa-building" /> {selected.organizationName || t('SupportConsole.unknownOrg', 'Unknown')}</span>
              <span><i className="fas fa-sign-in-alt" /> {selected.source === 'LOGIN_PAGE' ? t('SupportConsole.srcLogin', 'Login page') : t('SupportConsole.srcApp', 'In-app')}</span>
              <span><i className="fas fa-clock" /> {fmt(selected.createdAt)}</span>
            </div>
          </div>
          <div className="spc-status-ctl">
            <span className={`spc-badge spc-${selected.status.toLowerCase()}`}>{selected.status}</span>
            <select value={selected.status} onChange={(e) => changeStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="spc-original">
          <div className="spc-original-label">{t('SupportConsole.original', 'Original request')}</div>
          <p>{selected.message}</p>
        </div>

        <div className="spc-feed">
          {(selected.messages || []).map((m) => (
            m.authorType === 'SYSTEM' ? (
              <div key={m.id} className="spc-system">— {m.body} · {fmt(m.createdAt)} —</div>
            ) : (
              <div key={m.id} className={`spc-msg ${m.authorType === 'CUSTOMER' ? 'spc-customer' : 'spc-support'} ${m.internal ? 'spc-internal' : ''}`}>
                <div className="spc-msg-head">
                  {m.internal && <span className="spc-internal-tag"><i className="fas fa-lock" /> {t('SupportConsole.internalNote', 'Internal note')}</span>}
                  <strong>{m.authorName || m.authorEmail || m.authorType}</strong>
                  <span className="spc-msg-time">{fmt(m.createdAt)}</span>
                </div>
                <p>{m.body}</p>
              </div>
            )
          ))}
          {(selected.messages || []).length === 0 && (
            <div className="spc-system">{t('SupportConsole.noMessages', 'No conversation yet.')}</div>
          )}
        </div>

        <div className="spc-composer">
          <textarea rows={3} value={composer} onChange={(e) => setComposer(e.target.value)}
            placeholder={internal
              ? t('SupportConsole.notePh', 'Internal note — never visible to the customer…')
              : t('SupportConsole.replyPh', 'Reply to the customer…')} />
          <div className="spc-composer-row">
            <label className="spc-internal-check">
              <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
              {t('SupportConsole.markInternal', 'Internal note (hidden from customer)')}
            </label>
            <button className="spc-btn-primary" disabled={sending || !composer.trim()} onClick={sendMessage}>
              {sending ? t('SupportConsole.sending', 'Sending…')
                : internal ? t('SupportConsole.addNote', 'Add note') : t('SupportConsole.reply', 'Reply')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="spc-page">
      {error && <p className="spc-error">{error}</p>}
      <div className="spc-toolbar">
        <div className="spc-search">
          <i className="fas fa-search" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load(0)}
            placeholder={t('SupportConsole.searchPh', 'Search ticket #, email, name, organization…')} />
          <button className="spc-btn-primary" onClick={() => load(0)}>{t('SupportConsole.search', 'Search')}</button>
        </div>
        <div className="spc-filters">
          <button className={!statusFilter ? 'spc-chip spc-chip-on' : 'spc-chip'} onClick={() => setStatusFilter('')}>
            {t('SupportConsole.all', 'All')}
          </button>
          {STATUSES.map((s) => (
            <button key={s} className={statusFilter === s ? 'spc-chip spc-chip-on' : 'spc-chip'}
              onClick={() => setStatusFilter(s)}>{s}</button>
          ))}
        </div>
      </div>

      <table className="spc-table">
        <thead>
          <tr>
            <th>{t('SupportConsole.colTicket', 'Ticket')}</th>
            <th>{t('SupportConsole.colStatus', 'Status')}</th>
            <th>{t('SupportConsole.colRequester', 'Requester')}</th>
            <th>{t('SupportConsole.colOrg', 'Organization')}</th>
            <th>{t('SupportConsole.colMessage', 'Message')}</th>
            <th>{t('SupportConsole.colActivity', 'Last activity')}</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((tk) => (
            <tr key={tk.id} onClick={() => openTicket(tk.id)}>
              <td className="spc-ticketno">{tk.ticketNumber}</td>
              <td><span className={`spc-badge spc-${tk.status.toLowerCase()}`}>{tk.status}</span></td>
              <td>{tk.requesterName || tk.requesterEmail}</td>
              <td>{tk.organizationName || '—'}</td>
              <td className="spc-excerpt">{tk.excerpt}</td>
              <td>{fmt(tk.lastActivityAt)}</td>
            </tr>
          ))}
          {!loading && tickets.length === 0 && (
            <tr><td colSpan={6} className="spc-empty">{t('SupportConsole.empty', 'No tickets found.')}</td></tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="spc-pager">
          <button disabled={page === 0} onClick={() => load(page - 1)}>&larr;</button>
          <span>{page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => load(page + 1)}>&rarr;</button>
        </div>
      )}
    </div>
  );
}

export default SupportConsole;
